/**
 * Marketing-only design tokens.
 *
 * Scoped to the public landing pages (Hem, Produkt, Så funkar det,
 * Användningsområden, Om oss). Does NOT affect the authenticated app theme.
 *
 * Tone: editorial-Scandinavian. Cream + deep navy. Heavy whitespace.
 * Serif for display, sans-serif for body.
 */

export const marketingPalette = {
  // Background — warm off-white, calmer than #F8FAFC
  bg: '#F4EFE6',
  bgAlt: '#FFFFFF',
  bgMuted: '#EBE5D9',

  // Text — nearly-black navy. Pushed darker (#0A1620) for max legibility
  // against the cream bg, while staying inside the editorial palette.
  text: '#0A1620',
  textMuted: '#4A5A6B',
  textSubtle: '#7A8A99',

  // Borders — hairlines, never heavy
  border: '#D9D2C4',
  borderStrong: '#B8AE9B',

  // One restrained accent. Reused from the app's brand cyan but used sparingly.
  accent: '#0EA5E9',
  accentHover: '#0284C7',

  // Dark CTA banner background — slightly deeper than text colour for hierarchy
  ctaBg: '#0F1F2E',
  ctaText: '#FFFFFF',
  ctaTextMuted: 'rgba(255,255,255,0.72)',
} as const;

export const marketingFonts = {
  serif: '"Source Serif 4", Georgia, "Times New Roman", serif',
  sans: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  mono: '"SF Mono", Menlo, Monaco, Consolas, "Courier New", monospace',
} as const;

/**
 * Editorial type scale. Headlines are serif and lean (weight 400–500),
 * not bold-shouting. Body is sans, comfortable line-height.
 */
export const marketingType = {
  eyebrow: {
    fontFamily: marketingFonts.sans,
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.18em',
    textTransform: 'uppercase' as const,
    color: marketingPalette.textSubtle,
  },
  display: {
    fontFamily: marketingFonts.serif,
    fontWeight: 500,
    fontSize: 'clamp(2.6rem, 5vw, 4.2rem)',
    lineHeight: 1.05,
    letterSpacing: '-0.022em',
    color: marketingPalette.text,
  },
  h2: {
    fontFamily: marketingFonts.serif,
    fontWeight: 500,
    fontSize: 'clamp(2rem, 3.4vw, 2.8rem)',
    lineHeight: 1.12,
    letterSpacing: '-0.015em',
    color: marketingPalette.text,
  },
  h3: {
    fontFamily: marketingFonts.serif,
    fontWeight: 500,
    fontSize: '1.4rem',
    lineHeight: 1.28,
    color: marketingPalette.text,
  },
  lead: {
    fontFamily: marketingFonts.sans,
    fontWeight: 400,
    fontSize: '1.1rem',
    lineHeight: 1.62,
    color: marketingPalette.textMuted,
  },
  body: {
    fontFamily: marketingFonts.sans,
    fontWeight: 400,
    fontSize: '0.97rem',
    lineHeight: 1.7,
    color: marketingPalette.textMuted,
  },
  caption: {
    fontFamily: marketingFonts.sans,
    fontWeight: 400,
    fontSize: '0.82rem',
    lineHeight: 1.55,
    color: marketingPalette.textSubtle,
  },
} as const;

export const marketingLayout = {
  // Wider editorial canvas — content blocks remain narrower internally where
  // readability matters (lead paragraphs cap around 720px), but the page
  // shell breathes across modern desktop widths.
  pageMaxWidth: 1280,
  // Bumped ~30% on desktop to give sections more visual chapter separation
  // without resorting to alternating background bands.
  sectionPaddingY: { xs: 8, md: 14 },
  navHeight: 72,
} as const;
